import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { queryClient } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';

// Layout
import { AppShell } from '@/components/layout/AppShell';

// Navigation
import { PrivateRoute } from '@/navigation/PrivateRoute';
import { PublicRoute } from '@/navigation/PublicRoute';

// Pages
import { LoginPage } from '@/pages/LoginPage/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage/DashboardPage';
import { CompaniesPage } from '@/pages/CompaniesPage/CompaniesPage';
import { UsersPage } from '@/pages/UsersPage/UsersPage';
import { MachinesPage } from '@/pages/MachinesPage/MachinesPage';
import { ProductsPage } from '@/pages/ProductsPage/ProductsPage';
import { TransactionsPage } from '@/pages/TransactionsPage/TransactionsPage';
import { SessionsPage } from '@/pages/SessionsPage/SessionsPage';
import { RevenueReportsPage } from '@/pages/RevenueReportsPage/RevenueReportsPage';
import { SettingsPage } from '@/pages/SettingsPage/SettingsPage';
import { WalletsPage } from '@/pages/WalletsPage/WalletsPage';
import { CompanyDetailPage } from '@/pages/CompaniesPage/CompanyDetailPage';
import { MachineDetailPage } from '@/pages/MachinesPage/MachineDetailPage';
import { EmailsPage } from '@/pages/EmailsPage/EmailsPage';
import { EmailDetailPage } from '@/pages/EmailsPage/EmailDetailPage';
import { OrdersPage } from '@/pages/OrdersPage/OrdersPage';
import { OrderDetailPage } from '@/pages/OrdersPage/OrderDetailPage';

function AppRoutes() {
  const { checkAuth } = useAuth();

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        element={
          <PrivateRoute>
            <AppShell />
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/:id" element={<CompanyDetailPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="machines" element={<MachinesPage />} />
        <Route path="machines/:id" element={<MachineDetailPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="wallets" element={<WalletsPage />} />
        <Route path="revenue" element={<RevenueReportsPage />} />
        <Route path="emails" element={<EmailsPage />} />
        <Route path="emails/:id" element={<EmailDetailPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
